import type { ScriptProps } from "gatsby";
import { useEffect, useRef } from "react";
import { AppDispatchContext } from "../context/app/context";
import {
	type CookieConsent,
	useHasCookieConsent,
} from "../utils/cookies/cookie-consent-service";
import { useDispatchContext } from "./use-dispatch-context";

interface UseScriptProps {
	script: ScriptProps;
	removeOnUnmount: boolean;
	condition?: CookieConsent;
	shouldRender?: boolean;
}

const useScript = ({
	script,
	removeOnUnmount,
	condition,
	shouldRender = true,
}: UseScriptProps): void => {
	const appDispatch = useDispatchContext(AppDispatchContext);

	const scriptObject = useRef<ScriptProps>(script);

	if (removeOnUnmount && scriptObject.current.id) {
		scriptObject.current.id = `${
			scriptObject.current.id
		}-${Math.random().toString(36)}`;
	}

	const hasCookieConsent = useHasCookieConsent(condition ?? "strict");

	useEffect(() => {
		const currentScriptObject = scriptObject.current;
		let listener: (() => void) | undefined;
		if (!shouldRender) return;
		if (hasCookieConsent) {
			appDispatch({ type: "addScript", script: currentScriptObject });
		} else {
			listener = () => {
				appDispatch({ type: "addScript", script: currentScriptObject });
			};
			window.addEventListener(`CookieScriptCategory-${condition}`, listener);
		}

		return () => {
			if (removeOnUnmount) {
				appDispatch({ type: "removeScript", script: currentScriptObject });
			}
			if (listener) {
				window.removeEventListener(
					`CookieScriptCategory-${condition}`,
					listener,
				);
			}
		};
	}, [appDispatch, removeOnUnmount, condition, shouldRender, hasCookieConsent]);
};

export default useScript;
